'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  image: string;
  alt: string;
  quote: string;
  type: 'volunteer' | 'donor' | 'partner';
}

const TestimonialSection: React.FC = () => {
  const [activeType, setActiveType] = useState<'volunteer' | 'donor' | 'partner'>('volunteer');

  const testimonials: Testimonial[] = [
  {
    id: 'v1',
    name: 'Priya Sharma',
    role: 'Health Camp Volunteer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_139490e7a-1763296235599.png",
    alt: 'Young Indian woman with long black hair in blue medical scrubs smiling warmly',
    quote: 'Volunteering with Seva Setu has been the most rewarding experience of my life. Seeing the direct impact of our work on families in need fills my heart with joy and purpose.',
    type: 'volunteer'
  },
  {
    id: 'v2',
    name: 'Rahul Patel',
    role: 'Education Mentor',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1101e9026-1763296623238.png",
    alt: 'Young professional man with short dark hair in casual blue shirt smiling confidently',
    quote: 'Teaching underprivileged children has taught me more than I ever taught them. The trust and their eagerness to learn is truly inspiring. This organization makes volunteering seamless and impactful.',
    type: 'volunteer'
  },
  {
    id: 'd1',
    name: 'Anjali Desai',
    role: 'Monthly Donor',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18f15b753-1763301304170.png",
    alt: 'Middle-aged woman with glasses and warm smile in professional attire',
    quote: 'The transparency in how my donations are used gives me complete confidence. Every month, I receive detailed reports showing exactly how my contribution helped families access healthcare.',
    type: 'donor'
  },
  {
    id: 'd2',
    name: 'Vikram Mehta',
    role: 'Corporate Donor',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19255315f-1763296134162.png",
    alt: 'Professional businessman in navy suit with confident expression in modern office',
    quote: 'Supporting Seva Setu through our company CSR has been incredibly fulfilling. Their organized approach and measurable impact make them an ideal partner for corporate social responsibility.',
    type: 'donor'
  },
  {
    id: 'p1',
    name: 'Dr. Suresh Kumar',
    role: 'Healthcare Partner',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9c787bd-1763293571066.png",
    alt: 'Senior doctor in white coat with stethoscope and kind expression',
    quote: 'Partnering with Seva Setu has allowed us to extend our services to communities that need it most. Their professional coordination and genuine care for beneficiaries is commendable.',
    type: 'partner'
  },
  {
    id: 'p2',
    name: 'Neha Agarwal',
    role: 'Educational Institution Partner',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1978c955e-1763294317570.png",
    alt: 'Young female educator with friendly smile in casual professional attire',
    quote: 'Our students gain invaluable real-world experience through this partnership. Seva Setu provides structured volunteer opportunities that align perfectly with our community service curriculum.',
    type: 'partner'
  }];


  const filteredTestimonials = testimonials.filter((t) => t.type === activeType);

  const typeLabels = {
    volunteer: 'Volunteers',
    donor: 'Donors',
    partner: 'Partners'
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-3">
          Voices of Impact
        </h3>
        <p className="text-muted-foreground mb-6">
          Hear from those who make our mission possible
        </p>

        <div className="flex items-center justify-center space-x-2">
          {(Object.keys(typeLabels) as Array<keyof typeof typeLabels>).map((type) =>
          <button
            key={type}
            onClick={() => setActiveType(type)}
            className={`px-6 py-2 rounded-lg font-heading font-medium text-sm transition-all duration-300 ${
            activeType === type ?
            'bg-primary text-primary-foreground shadow-subtle' :
            'bg-muted text-foreground hover:bg-muted/80'}`
            }>

              {typeLabels[type]}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredTestimonials.map((testimonial) =>
        <div
          key={testimonial.id}
          className="bg-card rounded-xl shadow-elevated p-6 hover:shadow-cta transition-all duration-300">

            <div className="flex items-center space-x-4 mb-4">
              <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <AppImage
                src={testimonial.image}
                alt={testimonial.alt}
                className="w-full h-full object-cover" />

              </div>
              <div className="flex-1">
                <h4 className="font-heading font-bold text-foreground">{testimonial.name}</h4>
                <p className="text-sm text-muted-foreground">{testimonial.role}</p>
              </div>
              <Icon name="ChatBubbleLeftIcon" size={24} className="text-primary opacity-20" variant="solid" />
            </div>

            <p className="text-muted-foreground italic leading-relaxed">
              "{testimonial.quote}"
            </p>

            <div className="flex items-center space-x-1 mt-4">
              {[1, 2, 3, 4, 5].map((star) =>
            <Icon key={star} name="StarIcon" size={16} className="text-accent" variant="solid" />
            )}
            </div>
          </div>
        )}
      </div>
    </div>);

};

export default TestimonialSection;